import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import roadToMsiData from '../data/roadToMsi.json';
import lckData from '../data/lck.json';
import etoilesData from '../data/etoiles.json';
import Header from '../widgets/molecules/header';
import ListeCalendar from '../widgets/molecules/listeCalendar';
import DayMatches from '../widgets/molecules/dayMatches';
import MatchCalendar from '../widgets/atomes/matchCalendar';
import { fetchMatchesByPage } from '../services/lckApi';

function PlayoffSlot({ slot }) {
    const equipeData = slot.teamtag
        ? lckData.equipes.find(e => e.teamtag === slot.teamtag)
        : null;

    const statut = roadToMsiData.teamStatuts?.[slot.teamtag] ?? null;
    const isTopSeed = slot.seed <= 2;

    const bgClass = statut === 'qualifié'
        ? 'bg-green-900/20 border-green-900/40 hover:border-green-700/60'
        : statut === 'éliminé'
        ? 'bg-red-900/20 border-red-900/40 hover:border-red-700/60'
        : isTopSeed
        ? 'bg-[#1A1A1A] bg-opacity-70 border-[#421F69] hover:border-gray-500'
        : 'bg-[#1A1A1A] bg-opacity-70 border-gray-700 hover:border-gray-500';

    return (
        <div className={`rounded-lg p-3 border transition-all ${bgClass}`}>
            <div className="flex items-center gap-3">
                <span className={`text-base font-bold w-5 text-center flex-shrink-0 ${isTopSeed ? 'text-[#8B5CF6]' : 'text-gray-500'}`}>
                    {slot.seed}
                </span>
                {equipeData ? (
                    <>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/equipes/${equipeData.logo}`}
                            alt={equipeData.name}
                            className="w-8 h-8 object-contain flex-shrink-0 rounded"
                            onError={(e) => e.target.style.display = 'none'}
                        />
                        <div className="min-w-0">
                            <p className="font-semibold text-white text-sm truncate">{equipeData.name}</p>
                            <p className="text-xs text-gray-400">{equipeData.teamtag}</p>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="w-8 h-8 rounded border border-dashed border-gray-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-gray-600 text-xs">?</span>
                        </div>
                        <span className="text-gray-500 text-sm italic">TBD</span>
                    </>
                )}
            </div>
        </div>
    );
}

function MSISlot({ slot }) {
    const equipeData = slot.teamtag
        ? lckData.equipes.find(e => e.teamtag === slot.teamtag)
        : null;

    return (
        <div className="rounded-lg p-3 border border-yellow-600/50 bg-yellow-600/10 hover:bg-yellow-600/20 transition-all">
            <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-yellow-400 flex-shrink-0 w-14 leading-tight">
                    Seed {slot.seed}<br />LCK
                </span>
                {equipeData ? (
                    <>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/equipes/${equipeData.logo}`}
                            alt={equipeData.name}
                            className="w-8 h-8 object-contain flex-shrink-0 rounded"
                            onError={(e) => e.target.style.display = 'none'}
                        />
                        <div className="min-w-0">
                            <p className="font-semibold text-white text-sm truncate">{equipeData.name}</p>
                            <p className="text-xs text-gray-400">{equipeData.teamtag}</p>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="w-8 h-8 rounded border border-dashed border-yellow-600/50 flex items-center justify-center flex-shrink-0">
                            <span className="text-yellow-600/50 text-xs">?</span>
                        </div>
                        <span className="text-gray-500 text-sm italic">TBD</span>
                    </>
                )}
            </div>
        </div>
    );
}

function LCKRoadToMSIPage() {
    const navigate = useNavigate();
    const [matches, setMatches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openSection, setOpenSection] = useState('calendrier');
    const scrollContainerRef = useRef(null);
    const dayRefs = useRef({});
    const liens = etoilesData.etoiles.map(e => e.lien);
    const toggle = (section) => setOpenSection(s => s === section ? null : section);

    useEffect(() => {
        fetchMatchesByPage(roadToMsiData.overviewPage)
            .then(data => {
                setMatches(data);
                setIsLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setIsLoading(false);
            });
    }, []);

    const scrollToToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split('T')[0];
        const sortedDays = Object.keys(dayRefs.current).sort();
        const targetDay = sortedDays.find(d => d >= todayStr);
        if (!targetDay || !dayRefs.current[targetDay]) return;
        const targetEl = dayRefs.current[targetDay];
        const isMobile = window.innerWidth < 768;
        if (!isMobile && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = targetEl.offsetTop;
        } else {
            targetEl.scrollIntoView({ block: 'start' });
        }
    };

    useEffect(() => {
        if (isLoading) return;
        const isMobile = window.innerWidth < 768;
        if (!isMobile) setTimeout(scrollToToday, 0);
    }, [isLoading]);

    useEffect(() => {
        if (openSection !== 'calendrier' || isLoading) return;
        setTimeout(scrollToToday, 50);
    }, [openSection, isLoading]);

    const matchesByDay = matches.reduce((acc, match) => {
        if (!acc[match.date]) acc[match.date] = [];
        acc[match.date].push(match);
        return acc;
    }, {});

    const sortedDays = Object.keys(matchesByDay).sort();

    const confirmedCount = roadToMsiData.playoffSlots.filter(s => s.teamtag !== null).length;

    return (
        <>
            <div className="bg-image"></div>
            <div className="flex flex-col min-h-screen md:h-screen text-white relative">
                <Header etoiles={liens} />
                <div className="bg-[#1A1A1A] bg-opacity-70 backdrop-blur-sm border-b border-gray-700 px-4 md:px-8 py-4 md:py-6">
                    <div className="flex items-center gap-3 md:gap-6">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
                        >
                            <span className="text-3xl md:text-4xl">←</span>
                        </button>
                        <div className="flex-1 text-center">
                            <h1 className="text-lg md:text-2xl font-bold font-poppins">LCK - Road to MSI</h1>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-xl font-bold font-poppins">
                                {confirmedCount}
                                <span className="text-gray-500 text-base">/{roadToMsiData.playoffSlots.length}</span>
                            </p>
                            <p className="text-xs text-gray-400">qualifiés</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:flex-1 gap-4 md:gap-8 px-4 md:px-8 pt-4 md:pt-8 md:overflow-hidden w-full">
                    <div className="flex-1 md:overflow-hidden">
                        <div
                            className="flex justify-between items-center cursor-pointer md:hidden bg-[#272727] rounded-lg px-4 py-3 mb-2 sticky top-0 z-10"
                            onClick={() => toggle('calendrier')}
                        >
                            <span className="text-white font-semibold text-xl font-poppins">Calendrier</span>
                            <span className="text-gray-400 text-lg select-none">{openSection === 'calendrier' ? '▲' : '▼'}</span>
                        </div>
                        <div ref={scrollContainerRef} className={`md:h-full overflow-y-auto pb-8 ${openSection === 'calendrier' ? '' : 'hidden md:block'}`}>
                            {isLoading && (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Chargement des matchs...
                                </div>
                            )}
                            {error && (
                                <div className="flex items-center justify-center h-32 text-red-400">
                                    Erreur : {error}
                                </div>
                            )}
                            {!isLoading && !error && sortedDays.length === 0 && (
                                <div className="flex items-center justify-center h-32 text-gray-500">
                                    Aucun match disponible
                                </div>
                            )}
                            {!isLoading && !error && sortedDays.length > 0 && (
                                <ListeCalendar>
                                    {sortedDays.map((day) => (
                                        <div
                                            key={day}
                                            ref={(el) => { if (el) dayRefs.current[day] = el; }}
                                        >
                                            <DayMatches
                                                dateStr={day}
                                                matches={
                                                    <div className="flex flex-col gap-4">
                                                        {matchesByDay[day].map((match) => (
                                                            <MatchCalendar
                                                                key={match.id}
                                                                match={match}
                                                                equipes={lckData.equipes}
                                                                winScore={3}
                                                            />
                                                        ))}
                                                    </div>
                                                }
                                            />
                                        </div>
                                    ))}
                                </ListeCalendar>
                            )}
                        </div>
                    </div>

                    <div className="w-full md:w-[30%] md:overflow-hidden">
                        <div
                            className="flex justify-between items-center cursor-pointer md:hidden bg-[#272727] rounded-lg px-4 py-3 mb-2 sticky top-0 z-10"
                            onClick={() => toggle('classement')}
                        >
                            <span className="text-white font-semibold text-xl font-poppins">Qualifiés</span>
                            <span className="text-gray-400 text-lg select-none">{openSection === 'classement' ? '▲' : '▼'}</span>
                        </div>
                        <div className={`md:h-full overflow-y-auto py-4 md:py-6 ${openSection === 'classement' ? '' : 'hidden md:block'}`}>
                            <div className="space-y-3">
                                <h2 className="text-white font-semibold text-lg font-poppins px-1">Qualifiés</h2>
                                <div className="space-y-2">
                                    {roadToMsiData.playoffSlots.slice(0, 2).map((slot) => (
                                        <PlayoffSlot key={slot.seed} slot={slot} />
                                    ))}
                                </div>
                                <div className="flex items-center gap-2 px-1">
                                    <div className="h-px flex-1 bg-gray-700"></div>
                                    <span className="text-xs text-gray-500 uppercase tracking-wider">Lower Bracket</span>
                                    <div className="h-px flex-1 bg-gray-700"></div>
                                </div>
                                <div className="space-y-2">
                                    {roadToMsiData.playoffSlots.slice(2).map((slot) => (
                                        <PlayoffSlot key={slot.seed} slot={slot} />
                                    ))}
                                </div>

                                <div className="flex items-center gap-2 px-1 pt-2">
                                    <div className="h-px flex-1 bg-yellow-600/50"></div>
                                    <span className="text-xs text-yellow-400 uppercase tracking-wider font-semibold">→ MSI</span>
                                    <div className="h-px flex-1 bg-yellow-600/50"></div>
                                </div>
                                <div className="space-y-2">
                                    {roadToMsiData.msiSlots.map((slot) => (
                                        <MSISlot key={slot.seed} slot={slot} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LCKRoadToMSIPage;
